import React, { useState } from 'react';
import axios from 'axios';
import { Calendar, CheckCircle2, User, Phone, Sparkles, Loader2 } from 'lucide-react';
import { services, offices } from '../mock/mock';
import SectionHeading from './SectionHeading';

const initial = { name: '', phone: '', service: '', city: '', date: '', message: '' };

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const handle = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = 'Please enter your name';
    if (!/^[+\d][\d\s-]{7,}$/.test(form.phone.trim())) er.phone = 'Enter a valid phone number';
    if (!form.service) er.service = 'Select a service';
    if (!form.date) er.date = 'Pick a preferred date';
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setLoading(true);
    setApiError('');
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, form);
      setSubmitted(true);
    } catch {
      setApiError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full bg-[#fffaf0] border border-[#e2c887] rounded-lg px-4 py-3 font-body text-lg text-[#4a2f16] placeholder-[#b79a6a] focus:outline-none focus:border-[#c9962e] focus:ring-2 focus:ring-[#c9962e]/30 transition';

  return (
    <section id="booking" className="relative py-24 px-5 md:px-8">
      <div className="max-w-3xl mx-auto">
        <SectionHeading
          eyebrow="Begin Your Journey"
          title="Book Your Appointment"
          subtitle="Share your details and we will connect for a personalised consultation."
        />

        <div
          className="reveal rounded-3xl p-8 md:p-12 mt-14"
          data-delay="120"
          style={{
            background: 'linear-gradient(180deg, #fffaf0, #f6e9cd)',
            border: '1.5px solid #d9a63b',
            boxShadow: '0 20px 50px rgba(124,19,15,0.12)',
          }}
        >
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="mx-auto text-[#1f7a3d] mb-4" size={64} />
              <h3 className="font-display font-bold text-3xl text-[#7c130f]">
                Namaste, {form.name.split(' ')[0]}!
              </h3>
              <p className="font-body text-lg text-[#5a3a1c] mt-3 max-w-md mx-auto">
                Your appointment request for <strong>{form.service}</strong> on{' '}
                <strong>{form.date}</strong> has been received. Our team will call you at{' '}
                <strong>{form.phone}</strong> shortly to confirm.
              </p>
              <button
                onClick={() => {
                  setForm(initial);
                  setSubmitted(false);
                }}
                className="mt-7 px-7 py-2.5 rounded-full font-heading tracking-wide text-[#7c130f] bg-[#f4e6c8] hover:bg-[#ecd9a8] transition"
                style={{ border: '1.5px solid #c9962e' }}
              >
                Book Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 font-heading text-sm tracking-wide text-[#7c4a20] mb-2">
                    <User size={15} /> Full Name
                  </label>
                  <input name="name" value={form.name} onChange={handle} placeholder="Your name" className={inputCls} />
                  {errors.name && <p className="text-[#a71c14] text-sm mt-1 font-body">{errors.name}</p>}
                </div>
                <div>
                  <label className="flex items-center gap-2 font-heading text-sm tracking-wide text-[#7c4a20] mb-2">
                    <Phone size={15} /> Phone
                  </label>
                  <input name="phone" value={form.phone} onChange={handle} placeholder="+91 98765 43210" className={inputCls} />
                  {errors.phone && <p className="text-[#a71c14] text-sm mt-1 font-body">{errors.phone}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 font-heading text-sm tracking-wide text-[#7c4a20] mb-2">
                    <Sparkles size={15} /> Service
                  </label>
                  <select name="service" value={form.service} onChange={handle} className={inputCls}>
                    <option value="">Choose a service</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                  {errors.service && <p className="text-[#a71c14] text-sm mt-1 font-body">{errors.service}</p>}
                </div>
                <div>
                  <label className="flex items-center gap-2 font-heading text-sm tracking-wide text-[#7c4a20] mb-2">
                    Preferred City
                  </label>
                  <select name="city" value={form.city} onChange={handle} className={inputCls}>
                    <option value="">Any / Online</option>
                    {offices.map((o) => (
                      <option key={o.id} value={o.city}>{o.city}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 font-heading text-sm tracking-wide text-[#7c4a20] mb-2">
                  <Calendar size={15} /> Preferred Date
                </label>
                <input type="date" name="date" value={form.date} onChange={handle} className={inputCls} />
                {errors.date && <p className="text-[#a71c14] text-sm mt-1 font-body">{errors.date}</p>}
              </div>

              <div>
                <label className="font-heading text-sm tracking-wide text-[#7c4a20] mb-2 block">
                  Your Question (optional)
                </label>
                <textarea name="message" value={form.message} onChange={handle} rows={3} placeholder="Tell us briefly what guidance you seek..." className={inputCls} />
              </div>

              {apiError && (
                <p data-testid="booking-api-error" className="text-[#a71c14] text-base font-body text-center">{apiError}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-btn"
                className="w-full py-4 rounded-full font-heading tracking-[0.15em] text-[#fff8e7] text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(90deg, #a71c14, #7c130f, #a71c14)',
                  border: '1.5px solid #d9a63b',
                  boxShadow: '0 10px 30px rgba(124,19,15,0.4)',
                }}
              >
                {loading && <Loader2 className="animate-spin" size={20} />}
                {loading ? 'SUBMITTING...' : 'CONFIRM MY APPOINTMENT'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
